<template>
  <div>
    <v-tooltip location="bottom" open-delay="500">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="field.primaryKey || field.reference ? props : undefined"
          class="border-0 cursor-grab sortHandle"
          size="small"
          variant="plain"
          tabindex="-1"
          :icon="field.primaryKey ? 'mdi-key' : field.reference ? 'mdi-link' : 'mds:drag-indicator'"
          :disabled='store.state.isWorkflowDisabled'
        />
      </template>
      <span>{{ field.primaryKey ? 'Primary key' : 'Reference to entity' }}</span>
    </v-tooltip>
    <quick-rename-input
      :id="`edit-entity-${entity.id}-field-${index}`"
      class="flex-grow-1"
      :value="field.name"
      :density="`compact`"
      :max-length="30"
      @onChange="blurTableCell($event, entityIndex, index, 'name', null)"
      :class="{ 'required-field': field.tags?.includes('REQUIRED') }"
      :disabled="store.state.isWorkflowDisabled"
    />
    <v-tooltip v-if="field.reference && !relatedEntity" location="bottom" open-delay="100">
      <template v-slot:activator="{ props }">
        <v-icon v-bind="props" size="small" icon="mdi-alert" color="#ED1C0C" />
      </template>
      <span>Reference is missing related entity</span>
    </v-tooltip>
    <v-chip
      v-if="field.relatedEntityId && relatedEntity"
      density="compact"
      size="small"
      :closable="!store.state.isWorkflowDisabled"
      class="related-entity-chip px-2 ml-1"
    >
      {{ field.cardinality === 'one-to-many'
        ? `[ ${getRelatedEntity(field)?.name} ]`
        : `${getRelatedEntity(field)?.name}` }}
      <template v-if="!store.state.isWorkflowDisabled" #close>
        <v-icon
          icon="mdi-close-circle"
          color="#888"
          :size="16"
          class="remove-entity-icon"
          @click.stop="selectRelatedEntity(null, entityIndex, index)"
        />
      </template>
    </v-chip>
    <v-menu
      :model-value="openMenu"
      @update:modelValue="() => null"
      class="card-menu"
      @click:outside="openMenu = false"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          icon="mdi-dots-vertical"
          size="small"
          variant="plain"
          v-bind="props"
          @click="openMenu = !openMenu"
        />
      </template>
      <v-list density="compact">
        <select-entity
          @onSelectEntity="(selectedEntity) => selectRelatedEntity(selectedEntity, entityIndex, index)"
        >
          <v-list-item class="cursor-pointer">
            <v-list-item-title>
              <v-icon class="mr-2" color="grey">mdi-database</v-icon> Select Entity
            </v-list-item-title>
          </v-list-item>
        </select-entity>
        <v-list-item @click="setPrimaryKey(entityIndex, index)">
          <v-list-item-title>
            <v-icon class="mr-2" color="grey" :icon="field.primaryKey ? 'mdi-key-remove' : 'mdi-key'" />
            {{ field.primaryKey ? 'Unset primary key' : 'Set as primary key' }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="setReference(entityIndex, index)">
          <v-list-item-title>
            <v-icon class="mr-2" color="grey" :icon="field.reference ? 'mdi-link-off' : 'mdi-link'" />
            {{ field.reference ? 'Unset reference' : 'Set as reference' }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="setFieldMandatory(entityIndex, index, !(field.tags && field.tags.includes('REQUIRED')))">
          <v-list-item-title>
            <v-icon class="mr-2" color="grey" icon="mdi-asterisk" />
            {{ field.tags && field.tags.includes('REQUIRED') ? 'Set as optional' : 'Set as required' }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="deleteEntityField(entityIndex, index)">
          <v-list-item-title>
            <v-icon class="mr-2" color="grey" icon="mdi-trash-can-outline" /> Delete
          </v-list-item-title>
        </v-list-item>
        <v-divider></v-divider>
        <div class="d-flex justify-center pt-1">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <toggle
                v-bind="!field.relatedEntityId ? props : undefined"
                mandatory
                :value="field.cardinality || 'one-to-one'"
                :options="[
                  { value: 'one-to-one', label: '1 : 1' },
                  { value: 'one-to-many', label: '1 : N' }
                ]"
                :disabled="store.state.isWorkflowDisabled || !field.relatedEntityId"
                @update:modelValue="(cardinality) => selectCardinality(cardinality, entityIndex, field.relatedEntityId)"
              />
            </template>
            <span>Disabled because this field is not set to be a related entity.</span>
          </v-tooltip>
        </div>
      </v-list>
    </v-menu>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import QuickRenameInput from '@/components/QuickRenameInput.vue'
import useEntityActions from '@/composables/useEntityActions'
import useDomainModel from '@/composables/useDomainModel'

const { entity, field, index } = defineProps({ entity: Object, field: Object, index: Number })

const openMenu = ref(false)

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

const entityIndex = computed(() => (workflow.value.dataModels ?? []).findIndex((item) => item.id === entity?.id))
const relatedEntity = computed(() => getRelatedEntity(field))

const {
  blurTableCell,
  setPrimaryKey,
  setReference,
  deleteEntityField,
  selectRelatedEntity,
  selectCardinality,
  setFieldMandatory
} = useEntityActions()

const { getRelatedEntity } = useDomainModel()
</script>

<style scoped>
.card-menu .v-list-item:hover {
  background-color: var(--light-2) !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px 0 !important;
}

.required-field {
  &::after {
    content: ' *';
  }

  &:focus::after {
    visibility: hidden;
  }
}

:deep(.related-entity-chip) {
  position: relative;

  &&& .v-chip__close {
    margin-inline-start: 0;
    margin-inline-end: 0;
    position: absolute;
    right: 0;
    background: #e8e8e8;
    opacity: 0;
    transition: all 0.2s;
  }

  &:hover {
    &&& .v-chip__close {
      opacity: 1;
    }
  }
}
</style>
